import { cn } from '@/utils/tailwind';
import { AnimatePresence, motion } from 'framer-motion';
import { useEffect, useRef, useState } from 'react';
import { TabKey } from '../../_consts/reviewsConstants';
import { useReviews } from './ReviewsProvider';

interface TabCount {
  key: string;
  label: string;
  count: number;
}

interface ContentsTabProps {
  tabListWithCounts: TabCount[];
}

export const ContentsTab = ({ tabListWithCounts }: ContentsTabProps) => {
  const { currentTab, setCurrentTab } = useReviews();

  const tabRefs = useRef<Record<string, HTMLButtonElement | null>>({});
  const tabBarRef = useRef<HTMLDivElement | null>(null);

  const [indicator, setIndicator] = useState<{
    left: number;
    width: number;
    bottom: number;
  } | null>(null);

  // ✅ 클릭(탭 변경) 기준으로 인디케이터 위치 업데이트
  useEffect(() => {
    const key = currentTab;
    if (key && tabRefs.current[key] && tabBarRef.current) {
      const node = tabRefs.current[key]!;
      const containerRect = tabBarRef.current.getBoundingClientRect();
      const nodeRect = node.getBoundingClientRect();

      setIndicator({
        left: nodeRect.left - containerRect.left,
        width: nodeRect.width,
        bottom: containerRect.bottom - nodeRect.bottom,
      });
    }
  }, [currentTab, tabListWithCounts]);

  return (
    <div ref={tabBarRef} className="relative flex flex-wrap gap-x-[20px] gap-y-[10px]">
      <AnimatePresence>
        {indicator && (
          <motion.span
            layoutId="inquiryTabIndicator"
            className="absolute bottom-0 h-[2px] rounded-full bg-gr-800"
            style={{
              left: indicator.left,
              width: indicator.width,
            }}
            initial={{ opacity: 0 }}
            animate={{
              opacity: 1,
              transition: { type: 'spring', stiffness: 400, damping: 30 },
            }}
            exit={{ opacity: 0 }}
          />
        )}
      </AnimatePresence>

      {/* 탭 버튼들 */}
      {tabListWithCounts.map((tab, idx) => (
        <TabButton
          key={tab.key}
          tab={tab}
          idx={idx}
          currentTab={currentTab}
          setCurrentTab={setCurrentTab}
          tabRefs={tabRefs}
        />
      ))}
    </div>
  );
};

interface TabButtonProps {
  tab: TabCount;
  idx: number;
  currentTab: TabKey;
  setCurrentTab: (key: TabKey) => void;
  tabRefs: React.MutableRefObject<Record<string, HTMLButtonElement | null>>;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
}
export const TabButton = ({ tab, currentTab, setCurrentTab }: TabButtonProps) => {
  const handleClick = () => {
    setCurrentTab(tab.key as typeof currentTab);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const isActive = currentTab === tab.key;

  return (
    <button
      key={tab.key}
      className={cn(
        'group relative pb-[18px] transition-colors duration-300 Title3S18',
        'text-[clamp(14px,2.5vw,18px)] 875:text-[18px]',
        isActive ? 'text-gr-900' : 'text-gr-500 hover:text-gr-700',
      )}
      onClick={handleClick}
    >
      <div className="flex flex-row items-center gap-2">
        {tab.label} {tab.count}
      </div>

      {/* ✅ 클릭 시 하단 라인 애니메이션 */}
      <AnimatePresence>
        {isActive && (
          <motion.span
            layoutId="tab-indicator"
            className="absolute bottom-0 left-0 right-0 h-[2px] bg-gr-800"
            transition={{ type: 'spring', stiffness: 400, damping: 30 }}
          />
        )}
      </AnimatePresence>
    </button>
  );
};
